'use client';

import type { ReactNode, ComponentType, SVGProps } from 'react';
import {
  AlertTriangle,
  Info as InfoIcon,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import { cn } from '@/shared/lib/cn';

/**
 * Design tokens for notice banners — centralized so all banners share the same palette.
 */
const noticeBannerTokens = {
  warning: {
    bg: 'bg-[#FBFBE4] dark:bg-amber-900/20',
    border: 'shadow-[inset_0_0_0_1px_rgba(217,119,6,0.18)] dark:shadow-[inset_0_0_0_1px_rgba(180,83,9,0.45)]',
    title: 'text-foreground dark:text-amber-400',
    message: 'text-[#594D33] dark:text-amber-400',
    action: 'text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300',
  },
  info: {
    bg: 'bg-[#D6E6E6] dark:bg-teal-900/20',
    border: 'shadow-[inset_0_0_0_1px_rgba(7,141,134,0.18)] dark:shadow-[inset_0_0_0_1px_rgba(15,118,110,0.45)]',
    title: 'text-foreground dark:text-teal-400',
    message: 'text-[#2D3A3A] dark:text-teal-400',
    action: 'text-[#078D86] hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300',
  },
  error: {
    bg: 'bg-[#FEE2E2] dark:bg-red-900/20',
    border: 'shadow-[inset_0_0_0_1px_rgba(220,38,38,0.18)] dark:shadow-[inset_0_0_0_1px_rgba(153,27,27,0.5)]',
    title: 'text-foreground dark:text-red-400',
    message: 'text-[#7F1D1D] dark:text-red-400',
    action: 'text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300',
  },
  // Success shares the teal info palette — differentiation is by icon only.
  success: {
    bg: 'bg-[#D6E6E6] dark:bg-teal-900/20',
    border: 'shadow-[inset_0_0_0_1px_rgba(7,141,134,0.18)] dark:shadow-[inset_0_0_0_1px_rgba(15,118,110,0.45)]',
    title: 'text-foreground dark:text-teal-400',
    message: 'text-[#2D3A3A] dark:text-teal-400',
    action: 'text-[#078D86] hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300',
  },
} as const;

export type NoticeBannerVariant = keyof typeof noticeBannerTokens;

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

/** Default icon per semantic variant — mirrors Apple/Android NoticeBanner. */
const defaultIconByVariant: Record<NoticeBannerVariant, IconComponent> = {
  warning: AlertTriangle,
  info: InfoIcon,
  error: AlertCircle,
  success: CheckCircle2,
};

interface NoticeBannerProps {
  /** Semantic variant controlling colors */
  variant: NoticeBannerVariant;
  /** Primary text (optional — omit for body-only banners) */
  title?: ReactNode;
  /** Optional secondary text */
  message?: ReactNode;
  /** Optional action button/link on the right (desktop) or below (mobile) */
  action?: ReactNode;
  /** Optional icon override (uses variant default if omitted; pass null to hide icon) */
  icon?: IconComponent | null;
  /** Tailwind class for title font weight (default: font-medium) */
  titleWeight?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Unified notice banner for all warning/info/error/success messages.
 *
 * All inline banners across the app should use this component.
 *
 * @example
 * <NoticeBanner variant="warning" title="Title" message="Description" />
 *
 * @example
 * <NoticeBanner
 *   variant="warning"
 *   title={<>Subscription will be canceled on <span className="font-medium">April 5</span></>}
 *   message="You'll maintain access until then."
 *   action={<Button variant="ghost" size="sm">Resume</Button>}
 * />
 */
export function NoticeBanner({
  variant,
  title,
  message,
  action,
  icon,
  titleWeight = 'font-medium',
  className,
}: NoticeBannerProps) {
  const tokens = noticeBannerTokens[variant];
  const IconComp =
    icon === null ? null : icon ?? defaultIconByVariant[variant];

  const iconEl = IconComp ? (
    <IconComp className={cn('h-4 w-4 flex-shrink-0 mt-0.5', tokens.action)} />
  ) : null;

  // Tight line-height (~1.15) keeps multi-line notice text compact and
  // visually consistent with Android/Apple NoticeBanner.
  // Role tokens mirror Apple: title=formLabel, message=cardMeta.
  const body = (
    <div className="flex-1 min-w-0">
      {title != null && title !== '' && (
        <div className={cn('text-form-label leading-[1.15]', titleWeight, tokens.title)}>{title}</div>
      )}
      {/* notice body (14px): one point above the card-meta role for
          readability, at parity with Apple `noticeBody` / Android 14sp. */}
      {message && (
        <div className={cn('text-notice-body leading-[1.15]', title ? 'mt-1' : '', tokens.message)}>{message}</div>
      )}
    </div>
  );

  return (
    <div
      className={cn(
        'p-4 rounded-2xl',
        tokens.bg,
        tokens.border,
        className
      )}
    >
      {/* Desktop: icon + title/message on left, action on right */}
      <div className="hidden md:flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          {iconEl}
          {body}
        </div>
        {action}
      </div>

      {/* Mobile: stacked */}
      <div className="flex md:hidden flex-col gap-3">
        <div className="flex items-start gap-2">
          {iconEl}
          {body}
        </div>
        {action && <div className="w-full">{action}</div>}
      </div>
    </div>
  );
}

/** Get action button classes for a specific variant */
export function getNoticeBannerActionClasses(variant: NoticeBannerVariant): string {
  return noticeBannerTokens[variant].action;
}
